import React, { useEffect, useState } from "react";
import { Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { api } from "../../lib/api";

const errMsg = (e, fallback) => (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : fallback);

// Email → 6-digit code by email → onSignedIn(response data with {token, user}).
export default function EmailOtpForm({ onSignedIn }) {
  const [step, setStep] = useState("email"); // email | code
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const send = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/auth/otp/email/request", { email });
      setStep("code");
      setCode("");
      setCooldown(r.data.resend_after || 60);
      toast.success("If an account exists for this email, a code is on its way");
    } catch (err) { toast.error(errMsg(err, "Could not send the code")); } finally { setBusy(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/auth/otp/email/verify", { email, code });
      await onSignedIn(r.data);
    } catch (err) { toast.error(errMsg(err, "Invalid or expired code")); } finally { setBusy(false); }
  };

  if (step === "email") {
    return (
      <form onSubmit={send} className="space-y-4" data-testid="email-otp-form">
        <div className="relative">
          <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <Input required type="email" autoComplete="username" data-testid="email-otp-email" placeholder="Email ID"
            value={email} onChange={(e) => setEmail(e.target.value)}
            className="pl-10 h-12 bg-gray-50 border-gray-200 focus-visible:ring-orange-500" />
        </div>
        <Button type="submit" disabled={busy || !email.trim()} data-testid="email-otp-send"
          className="w-full h-12 f360-gradient text-white font-semibold text-base border-0 hover:opacity-95">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2.5} /> : "Send code by email"}
        </Button>
      </form>
    );
  }

  return (
    <form onSubmit={verify} className="space-y-4" data-testid="email-otp-code-form">
      <p className="text-sm text-gray-600">Enter the 6-digit code sent to <b>{email}</b>. It expires in 10 minutes.</p>
      <Input required inputMode="numeric" autoComplete="one-time-code" maxLength={6} data-testid="email-otp-code"
        value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
        placeholder="••••••" className="bg-gray-50 border-gray-200 h-12 text-center text-2xl tracking-[0.5em] font-mono" />
      <Button type="submit" disabled={busy || code.length !== 6} data-testid="email-otp-verify"
        className="w-full h-12 f360-gradient text-white font-semibold text-base border-0 hover:opacity-95">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2.5} /> : "Verify & Sign In"}
      </Button>
      <div className="flex items-center justify-between text-xs">
        <button type="button" onClick={() => setStep("email")} className="text-gray-500 hover:text-gray-800">Change email</button>
        <button type="button" disabled={cooldown > 0 || busy} onClick={send} className="text-orange-600 hover:text-orange-700 disabled:text-gray-400">
          {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
        </button>
      </div>
    </form>
  );
}
